import { ACCESSIBILITY_OPTIONS } from '../config/options';
import StateManager from '../core/StateManager';

export default class TextToSpeech {
  constructor(sidebarElement) {
    this.active = false;
    this.sidebar = sidebarElement;
    
    // UI Elements
    this.toolbar = null;
    this.modeBtns = [];
    this.speedBtns = [];

    // State
    this.mode = 'click'; // click | hover
    this.speed = 1.0;
    this.puterLoaded = false;
    this.currentAudio = null;
    this.hoverTimeout = null;
    this.latestRequestId = 0; // To track pending requests

    // Bindings
    this.clickHandler = this.handleClick.bind(this);
    this.hoverHandler = this.handleHover.bind(this);
    this.mouseOutHandler = this.handleMouseOut.bind(this);
  }

  async loadPuter() {
    if (this.puterLoaded || window.puter) {
      this.puterLoaded = true;
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.puter.com/v2/';
      script.onload = () => {
        this.puterLoaded = true;
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async enable() {
    if (this.active) return;
    this.active = true;

    // Cache toolbar elements
    this.toolbar = document.querySelector('.js-a11y-tts-toolbar');
    if (this.toolbar) {
      this.modeBtns = this.toolbar.querySelectorAll('.js-tts-mode');
      this.speedBtns = this.toolbar.querySelectorAll('.js-tts-speed');
      
      // Bind Toolbar Events
      this.modeBtns.forEach(btn => btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.setMode(e.currentTarget.dataset.mode);
      }));
      
      this.speedBtns.forEach(btn => btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.setSpeed(parseFloat(e.currentTarget.dataset.speed));
      }));
      
      this.toolbar.classList.add('is-visible');
      this.updateToolbarUI(); // Ensure initial state matches
    }

    try {
      await this.loadPuter();
      this.attachListeners();
    } catch (e) {
      console.error('Failed to load Puter.js', e);
    }
  }

  disable() {
    if (!this.active) return;
    
    this.detachListeners();
    this.stopPlayback();

    if (this.toolbar) {
      this.toolbar.classList.remove('is-visible');
    }

    this.active = false;
  }

  attachListeners() {
    // Remove existing to avoid duplicates
    document.removeEventListener('click', this.clickHandler);
    document.removeEventListener('mouseover', this.hoverHandler);
    document.removeEventListener('mouseout', this.mouseOutHandler);

    if (this.mode === 'click') {
      document.addEventListener('click', this.clickHandler);
    } else {
      document.addEventListener('mouseover', this.hoverHandler);
      document.addEventListener('mouseout', this.mouseOutHandler);
    }
  }

  detachListeners() {
    document.removeEventListener('click', this.clickHandler);
    document.removeEventListener('mouseover', this.hoverHandler);
    document.removeEventListener('mouseout', this.mouseOutHandler);
  }

  setMode(mode) {
    if (this.mode === mode) return;
    this.mode = mode;
    this.updateToolbarUI();
    this.stopPlayback();
    this.attachListeners();
  }

  setSpeed(speed) {
    this.speed = speed;
    this.updateToolbarUI();
    this.stopPlayback();
  }

  updateToolbarUI() {
    if (!this.toolbar) return;
    this.modeBtns.forEach(btn => btn.classList.toggle('is-active', btn.dataset.mode === this.mode));
    this.speedBtns.forEach(btn => btn.classList.toggle('is-active', parseFloat(btn.dataset.speed) === this.speed));
  }

  stopPlayback() {
    // Increment request ID to invalidate any pending async requests
    this.latestRequestId++;

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0; // Reset
      this.currentAudio = null;
    }

    document.body.style.cursor = '';
  }

  async requestSpeechAudio(text) {
    const baseOptions = {
      provider: 'openai',
      model: 'gpt-4o-mini-tts',
      voice: 'alloy',
      language: 'pl-PL',
      instructions: 'Czytaj wyłącznie po polsku, z naturalnym polskim akcentem i polską fonetyką. Nie przełączaj się na inne języki, nawet jeśli tekst zawiera angielskie słowa, marki lub nazwy własne. Zawsze zachowuj polską wymowę: czytaj obce wyrazy fonetycznie po polsku. Tempo mowy powinno być spokojne, wyraźne i zrozumiałe zgodnie z WCAG, z czytelną dykcją i umiarkowanymi pauzami. Nigdy nie tłumacz treści ani nie dodawaj własnych słów.',
      speed: this.speed,
    };

    return await window.puter.ai.txt2speech(text, baseOptions);
  }

  // --- Handlers ---

  handleHover(e) {
    if (!this.isValidTarget(e.target)) return;

    // Clear previous pending read
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);

    // Debounce hover reading (wait 500ms)
    this.hoverTimeout = setTimeout(() => {
        this.readElement(e.target);
    }, 500);
  }

  handleMouseOut(e) {
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
    // Note: We don't stop playback on mouseout, usually you want to finish the sentence.
    // But if the user requested "should stop previous", we handle that in readElement's stopPlayback call.
  }

  handleClick(e) {
    // If clicking a toolbar button, don't read it
    if (this.toolbar && this.toolbar.contains(e.target)) return;

    if (!this.isValidTarget(e.target)) return;
    
    e.preventDefault();
    e.stopPropagation();
    this.readElement(e.target);
  }

  isValidTarget(target) {
    if (!document.documentElement.classList.contains('a11y-text-to-speech')) return false;
    if (this.sidebar.contains(target)) return false; // Ignore sidebar
    
    // Ignore interactive elements if in hover mode to allow normal interaction? 
    // Usually hover reading is for everything.
    
    // Must be text element or image
    return target.closest('p, h1, h2, h3, h4, h5, h6, li, a, span, div, img');
  }

  async readElement(target) {
    this.stopPlayback(); // Stop anything currently playing OR pending
    const requestId = this.latestRequestId; // Capture this request's ID

    let text = '';
    
    // Extract text based on element type
    if (target.tagName === 'IMG') {
        text = target.alt || target.title;
        if (text) text = `Obraz: ${text}`; // Adding "Obraz" context manually if desired, or leaving clean.
    } else if (['INPUT', 'TEXTAREA'].includes(target.tagName)) {
        text = target.value || target.placeholder;
    } else {
        text = target.innerText || target.textContent;
    }

    if (!text) return;

    // Loading State
    const originalCursor = document.body.style.cursor;
    document.body.style.cursor = 'wait';

    try {
        if (!this.puterLoaded) await this.loadPuter();

        const safeText = text.substring(0, 1000); 
        
        // Check if this request is still valid (not cancelled)
        if (requestId !== this.latestRequestId) return;

        if (requestId !== this.latestRequestId) return;

        // Use OpenAI with Speed param
        const audio = await this.requestSpeechAudio(safeText);

        // Check again after await
        if (requestId !== this.latestRequestId) return;

        this.currentAudio = audio;
        audio.play();

        audio.onended = () => {
          if (this.currentAudio === audio) {
             this.currentAudio = null;
          }
        };
    } catch (error) {
        if (requestId === this.latestRequestId) {
            console.error('Puter TTS Error:', error);
        }
    } finally {
        if (requestId === this.latestRequestId) { // Only reset cursor if not cancelled
            document.body.style.cursor = originalCursor;
        }
    }
  }

  async speakSystemMessage(text) {
      this.stopPlayback();
      const requestId = this.latestRequestId;
      
      // System messages are always Polish
      const finalText = `Lektor: ${text}`; 
      
       try {
           if (requestId !== this.latestRequestId) return;

           const audio = await this.requestSpeechAudio(finalText);
           
           if (requestId !== this.latestRequestId) return;
           
           this.currentAudio = audio;
           audio.play();
           
           return new Promise(resolve => {
               audio.onended = resolve;
           });
       } catch (e) {
           console.error("System TTS Error:", e);
       }

  }
}
